import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Crown } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('reseller_token')}` });
const MEDALHAS = ['🥇', '🥈', '🥉'];

/** Top 10 compradores do afiliado na semana (seg→dom) ou no mês. */
const TopBuyersCard = ({ resellerId }) => {
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!resellerId) return;
    let ativo = true;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/reseller/${resellerId}/top-buyers`, {
        params: { period }, headers: authHeaders(),
      })
      .then((r) => ativo && setData(r.data))
      .catch(() => ativo && setData(null))
      .finally(() => ativo && setLoading(false));
    return () => { ativo = false; };
  }, [resellerId, period]);

  const periodoLabel = data?.period === 'month'
    ? (data.month || '').split('-').reverse().join('/')
    : `${(data?.week_start || '').split('-').reverse().slice(0, 2).join('/')} a ${
      (data?.week_end || '').split('-').reverse().slice(0, 2).join('/')}`;

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-amber-200 shadow-sm"
      data-testid="top-buyers-card">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-2.5 text-white">
        <h4 className="flex items-center gap-1.5 text-sm font-bold">
          <Crown className="h-4 w-4" /> Top 10 compradores
          {data && <span className="text-[11px] font-medium opacity-90">· {periodoLabel}</span>}
        </h4>
        <div className="flex gap-1">
          {[['week', 'Semana'], ['month', 'Mês']].map(([v, label]) => (
            <Button key={v} size="sm" onClick={() => setPeriod(v)}
              data-testid={`top-buyers-period-${v}`}
              className={`h-7 px-3 text-xs ${period === v
                ? 'bg-white text-orange-600 hover:bg-white'
                : 'border border-white/50 bg-transparent text-white hover:bg-white/20'}`}>
              {label}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="py-6 text-center text-sm text-gray-400" data-testid="top-buyers-loading">
          Carregando…
        </p>
      ) : !data?.buyers?.length ? (
        <p className="py-6 text-center text-sm text-gray-500" data-testid="top-buyers-empty">
          Nenhuma compra no seu link {period === 'week' ? 'nesta semana' : 'neste mês'} ainda.
        </p>
      ) : (
        <div className="divide-y bg-white">
          {data.buyers.map((b) => (
            <div key={b.kako_id} className="flex items-center gap-3 p-2.5 hover:bg-amber-50/50"
              data-testid={`top-buyer-${b.kako_id}`}>
              <span className="w-7 shrink-0 text-center text-sm font-bold text-gray-500">
                {MEDALHAS[b.rank - 1] || `${b.rank}º`}
              </span>
              {b.avatar ? (
                <img src={b.avatar} alt={b.nickname || b.kako_id}
                  className="h-9 w-9 shrink-0 rounded-full border-2 border-amber-200 object-cover" />
              ) : (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-700">
                  {(b.nickname || b.kako_id || '?').slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900">
                  {b.nickname || `KAKO ${b.kako_id}`}
                </p>
                <p className="font-mono text-[11px] text-gray-500">
                  ID {b.kako_id} · {b.orders} compra{b.orders > 1 ? 's' : ''}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="whitespace-nowrap text-sm font-bold text-purple-600">
                  {(b.coins || 0).toLocaleString()} 🪙
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopBuyersCard;
